import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import Service from '@ember/service';
import type PartnerService from './partner';
import type SessionContextService from './session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type { AccountingTransaction as AccountingTransactionModel } from 'tio-data/data/resources/accounting-transaction.type';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import { query } from '@warp-drive/utilities/json-api';
import {
  accountingPaymentsRequest,
  accountingPreviewRequest,
} from 'tio-data/requests/employee-actions';
import { getSlrAlertsRequest } from 'tio-data/requests/person';
import type { SlrAlertsResponse } from 'tio-data/requests/person';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import { SLR_LOAN_STANDING_FEATURE_FLAG } from 'tio-employee/types/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';

export interface PastContributionPayment {
  amount: number;
  line_item_date: string;
  line_item_period: string;
  line_item_type: string;
  transaction_date?: string | null;
  servicer: string;
}

/**
 * Raw JSON returned by GET /employees/{id}/accounting_payments. Replaces the
 * untyped result the legacy `getAccountingPaymentContributions` adapter method
 * returned; the getters below read `.payments` / `.summary` off it.
 */
export interface AccountingPaymentContributions {
  payments?: PastContributionPayment[];
  summary?: { total: number };
}

export enum PaymentValidationErrorKey {
  ACCOUNTS_UNPAYABLE = 'ACCOUNTS_UNPAYABLE',
  NO_LOAN_BALANCE = 'NO_LOAN_BALANCE',
  EXCEEDED_LIMIT = 'EXCEEDED_LIMIT',
  NOT_STARTED_PLAN = 'NOT_STARTED_PLAN',
  NO_PLAN_CONTRIBUTION = 'NO_PLAN_CONTRIBUTION',
  INVALID_LOAN_TYPE = 'INVALID_LOAN_TYPE',
  AGGREGATION_INVALID_LOAN_TYPE = 'AGGREGATION_INVALID_LOAN_TYPE',
  NO_EMPLOYEE_TRANSACTIONS = 'NO_EMPLOYEE_TRANSACTIONS',
  TERMS_NOT_ACCEPTED = 'TERMS_NOT_ACCEPTED',
  STALE_ACCOUNT = 'STALE_ACCOUNT',
  NAME_MISMATCH = 'NAME_MISMATCH',
  INVALID_LOAN_STATUS = 'INVALID_LOAN_STATUS',
}

export interface PaymentValidationError {
  key: PaymentValidationErrorKey;
  message: string;
  source: string;
}

export interface FutureContributionPayment {
  amount: number;
  date: string;
  state: string;
  period: string;
  validation_errors: PaymentValidationError[];
}

export default class EmployeeService extends Service {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare features: FeaturesService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  get employee() {
    return this.sessionContext.currentEmployee;
  }

  get current() {
    return this.sessionContext.currentEmployee;
  }

  get mergedAdditionFields() {
    return this.employee?.employeeAddition?.editableMergedFields;
  }

  get contributionPayments(): PastContributionPayment[] {
    return this.contributionsState.value?.payments || [];
  }

  get contributionSummary() {
    return this.contributionsState.value?.summary;
  }

  get futureContributionPayments(): FutureContributionPayment[] {
    return this.futurePayments.value || [];
  }

  get hasScholarships() {
    const scholarships = this.employee?.scholarships;

    if (scholarships?.length) {
      const validScholarship = scholarships.find((scholarship) => {
        if (!scholarship.thruDate) {
          return true;
        }
        const thruDate = new Date(scholarship.thruDate).getTime();
        const nowDate = new Date().getTime();
        return thruDate > nowDate;
      });
      return !!validScholarship;
    }

    return false;
  }

  get hasContributions() {
    return !!this.contributionPayments.length;
  }

  get isTaOnlyEmployee() {
    const companyKey = this.employee?.plan?.companyKey ?? '';
    const taOnlyPlanId = this.partner.companySettings?.tuitionAssistance?.taOnlyPlanId ?? '';
    return !!(taOnlyPlanId && companyKey.includes(taOnlyPlanId));
  }

  get isLoadedEmployee() {
    return !!this.employee?.id;
  }

  get needsToLinkLoans() {
    return !this.employee.needsToLinkLoans;
  }

  get hasRogueFein() {
    return this.employee.pslfProfile?.feinIsRogue;
  }

  get documents() {
    return this.documentsData?.value || [];
  }

  get isPartiallyApplied() {
    return (
      !this.employee?.person?.accountReceivingContributions &&
      this.documents?.some((doc) => doc.reflectionState === 'ReflectionState.PARTIALLY_APPLIED')
    );
  }

  /**************************
   **  JSONAPI Query Methods **
   **************************/

  /**
   * Fetches contribution transactions via JSONAPI with pagination, sorting, and filtering.
   * Replaces the legacy adapter passthrough to tio-payments.
   *
   * Queries `accounting-transactions` filtered by employee, with the accounting-line-item
   * relationship included for period/type data.
   */
  async queryContributionTransactions(options?: {
    page?: { number?: number; size?: number };
    sort?: string;
  }) {
    const employee = this.employee;
    if (!employee?.id) {
      return { transactions: [] as AccountingTransactionModel[], meta: {} };
    }

    const pageNumber = options?.page?.number ?? 1;
    const pageSize = options?.page?.size ?? 6;

    const { content } = (await this.store.request(
      query<AccountingTransactionModel>('accounting-transaction', {
        'filter[employee]': employee.id,
        include: 'accounting-line-item',
        sort: options?.sort || '-transaction-date',
        'page[offset]': (pageNumber - 1) * pageSize,
        'page[limit]': pageSize,
      })
    )) as { content: { data: AccountingTransactionModel[]; meta?: Record<string, unknown> } };

    return {
      transactions: content.data,
      // JSONAPI pagination meta is available via content.meta
      meta: content.meta || {},
    };
  }

  /**************************
   **  Tracked Functions   **
   **************************/

  contributionsState = trackedFunction(this, async () => {
    const employee = this.employee;
    if (!employee) {
      return;
    }
    try {
      // WarpDrive builder read (wave 1.5) — replaces the custom employee
      // adapter `getAccountingPaymentContributions` method.
      // PLURAL /employees/{id}/accounting_payments; demoMode honoured natively.
      const response = await this.store.request(accountingPaymentsRequest(employee.id!));
      return response.content as AccountingPaymentContributions;
    } catch (e) {
      console.warn(e);
    }
  });

  futurePayments = trackedFunction(this, async () => {
    const employee = this.employee;
    if (employee.plan && employee.plan.cadence !== 'NEVER') {
      try {
        // WarpDrive builder read (wave 1.5) — replaces the custom employee
        // adapter `getAccountingFuturePayments` method.
        // PLURAL /employees/{id}/accounting_preview; demoMode honoured natively.
        const response = await this.store.request(accountingPreviewRequest(employee.id!));
        return response.content as FutureContributionPayment[];
      } catch (e) {
        console.warn(e);
      }
    } else {
      return;
    }
  });

  documentsData = trackedFunction(this, async () => {
    if (!this.employee?.person) {
      return;
    }
    if (this.employee.person.activeAccounts.length) {
      // only need to query for documents to check for partially applied if no active accounts exist
      return;
    }
    const { content } = (await this.store.request(
      query<ObservableDocumentModel>('observable-document', {
        'filter[person]': this.employee.person.id,
        sort: '-created-at',
        'page[limit]': 10,
      })
    )) as { content: { data: ObservableDocumentModel[] } };
    return content.data;
  });

  /**
   * Server-driven SLR account-setup alerts from /people/:id/slr-alerts — the
   * same source of truth the reimagined loans page uses. Replaces the deleted
   * client-side accountAlerts heuristics (#9445), which could contradict the
   * server (e.g. flag a "missing" number the payment layer could already use).
   * Returns undefined while loading and [] on failure — a fetch error must not
   * flip the dashboard tile into its error state.
   */
  slrAlertsData = trackedFunction(this, async () => {
    // No SLR plan → nothing to fetch (mirrors the futurePayments guard);
    // skipping the request avoids pointless /slr-alerts traffic for every
    // non-SLR employee who lands on the dashboard.
    const employee = this.employee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') {
      return [];
    }
    // The session USER's person, not employee.person: the API's slr_alerts
    // policy authorizes `user.person == record`, and the other /slr-alerts
    // callers already resolve the id this way.
    const personId = this.sessionContext.person?.id;
    if (!personId) {
      return [];
    }
    try {
      const { content } = (await this.store.request(getSlrAlertsRequest(personId))) as {
        content: SlrAlertsResponse;
      };
      return content.alerts ?? [];
    } catch (e) {
      console.warn('[employee] Failed to fetch SLR alerts', e);
      return [];
    }
  });

  get hasSlrAccountAlerts() {
    return (this.slrAlertsData.value?.length ?? 0) > 0;
  }

  /**
   * The loan-standing verification audit state for the receiving account —
   * the dashboard tile's third error source (the "Can't Process Payment"
   * state, which the SLR alerts spec assigns to every audit-window row,
   * alongside /slr-alerts and partially-applied). Undefined while loading,
   * null on failure/dark flag — a fetch error must not flip the tile into
   * its error state.
   */
  slrLoanStandingData = trackedFunction(this, async () => {
    if (this.features.isDisabled(SLR_LOAN_STANDING_FEATURE_FLAG)) return null;
    const employee = this.employee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') return null;
    try {
      return await this.slrLoanStandingVerification.fetchCurrentState();
    } catch (e) {
      console.warn('[employee] Failed to fetch SLR loan-standing state', e);
      return null;
    }
  });

  get hasSlrLoanStandingAttention() {
    return shouldShowSlrLoanStandingAlert(this.slrLoanStandingData.value);
  }
}
